import { UserProfile } from './AuthContext';

export type Portal = 'superadmin' | 'consultancy' | 'agent' | 'student';

const PORTAL_DASHBOARD: Record<Portal, string> = {
  superadmin: '/superadmin/dashboard',
  consultancy: '/dashboard',
  agent: '/agent-portal/dashboard',
  student: '/student-portal/dashboard',
};

/** Same rule as the backend (app/core/rbac.py): anything that is not superadmin, agent or student is consultancy staff. */
export const portalForRole = (role?: string | null): Portal => {
  const normalized = (role || '').trim().toLowerCase();
  if (normalized === 'superadmin' || normalized === 'super_admin') return 'superadmin';
  if (normalized === 'agent' || normalized === 'student') return normalized;
  return 'consultancy';
};

/** The portal the backend resolved for this user (falls back to the role for older sessions). */
export const getUserPortal = (user: Pick<UserProfile, 'role' | 'portal'> | null | undefined): Portal =>
  user?.portal ?? portalForRole(user?.role);

/** The dashboard route of the user's portal. Used after login and when a guard turns a user away. */
export const getUserDashboard = (user: Pick<UserProfile, 'role' | 'portal'> | null | undefined): string =>
  PORTAL_DASHBOARD[getUserPortal(user)];

/**
 * Checks if a user has permission to access a specific resource/feature.
 * Permissions are the SINGLE source of truth — no role-based bypasses except superadmin.
 *
 * @param user Current UserProfile object from AuthContext
 * @param requiredPermissions Single permission string or array of permissions (OR logic)
 */
export const hasPermission = (
  user: UserProfile | null,
  requiredPermissions?: string | string[]
): boolean => {
  if (!user) return false;

  // Super Admin role or wildcard permission has access to everything
  // Only a full Super Admin holds "*"; platform staff work in the same portal with the permissions of their role.
  if (user.permissions?.includes('*')) {
    return true;
  }

  // If no specific permission is required, allow access
  if (!requiredPermissions || (Array.isArray(requiredPermissions) && requiredPermissions.length === 0)) {
    return true;
  }

  const userPerms = user.permissions || [];
  const reqs = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

  // Check if user has at least one matching permission (OR logic)
  return reqs.some(req => {
    if (userPerms.includes(req) || userPerms.includes('*')) return true;

    // module:manage grants access to all actions under that module (e.g. countries:manage → countries:view)
    const modulePrefix = req.split(':')[0];
    return userPerms.includes(`${modulePrefix}:manage`);
  });
};
